import ListWrapper from "../utils/ListWrapper";
import DateTime from "../core/DateTime";
import UserRole from "../classes/UserRole";
import DFile from "../classes/DFile";
import BaseUser from "./BaseUser";
import DBObject from "../utils/DBObject";
import MicroSoftAcc from "./MicroSoftAcc";
import CloneContext from "../utils/CloneContext";
import CollectionUtils from "../utils/CollectionUtils";
import Company from "./Company";

export default class User extends BaseUser {
  private static readonly _COMPANY: number = 0;
  private static readonly _CREATEDDATE: number = 1;
  private static readonly _EMAIL: number = 2;
  private static readonly _FIRSTNAME: number = 3;
  private static readonly _ISREMEMBERME: number = 4;
  private static readonly _LASTHEARTBEAT: number = 5;
  private static readonly _LASTNAME: number = 6;
  private static readonly _MACCOUNT: number = 7;
  private static readonly _ONLINE: number = 8;
  private static readonly _PASSWORD: number = 9;
  private static readonly _PHONENUMBER: number = 10;
  private static readonly _PROFILE: number = 11;
  private static readonly _REPORTINGMANAGER: number = 12;
  private static readonly _ROLE: number = 13;
  private static readonly _STATUS: number = 14;
  private static readonly _SUBORDINATES: number = 15;
  private static readonly _TWILIONUMBER: number = 16;
  private _firstName: string = "";
  private _lastName: string = "";
  private _email: string = "";
  private _password: string = "";
  private _phoneNumber: string = "";
  private _role: UserRole = UserRole.SalesPerson;
  private _status: boolean = true;
  private _profile: DFile = null;
  private _isRememberMe: boolean = false;
  private _twilioNumber: string = "";
  private _subordinates: Array<User> = ListWrapper.inverse(
    this,
    "subordinates",
    User._SUBORDINATES
  );
  private _mAccount: MicroSoftAcc = null;
  private _reportingManager: User = null;
  private _lastHeartBeat: DateTime = null;
  private _online: boolean = false;
  private _company: Company = null;
  private _createdDate: DateTime = null;
  public constructor(
    d3eParams?: Partial<{
      company: Company;
      createdDate: DateTime;
      email: string;
      firstName: string;
      isRememberMe: boolean;
      lastHeartBeat: DateTime;
      lastName: string;
      mAccount: MicroSoftAcc;
      online: boolean;
      password: string;
      phoneNumber: string;
      profile: DFile;
      reportingManager: User;
      role: UserRole;
      status: boolean;
      subordinates: Array<User>;
      twilioNumber: string;
    }>
  ) {
    super();

    this.setCompany(d3eParams?.company ?? null);

    this.setCreatedDate(d3eParams?.createdDate ?? null);

    this.setEmail(d3eParams?.email ?? "");

    this.setFirstName(d3eParams?.firstName ?? "");

    this.setIsRememberMe(d3eParams?.isRememberMe ?? false);

    this.setLastHeartBeat(d3eParams?.lastHeartBeat ?? null);

    this.setLastName(d3eParams?.lastName ?? "");

    this.setMAccount(d3eParams?.mAccount ?? null);

    this.setOnline(d3eParams?.online ?? false);

    this.setPassword(d3eParams?.password ?? "");

    this.setPhoneNumber(d3eParams?.phoneNumber ?? "");

    this.setProfile(d3eParams?.profile ?? null);

    this.setReportingManager(d3eParams?.reportingManager ?? null);

    this.setRole(d3eParams?.role ?? UserRole.SalesPerson);

    this.setStatus(d3eParams?.status ?? true);

    this.setSubordinates(
      d3eParams?.subordinates ??
        ListWrapper.inverse(this, "subordinates", User._SUBORDINATES)
    );

    this.setTwilioNumber(d3eParams?.twilioNumber ?? "");
  }
  public get d3eType(): string {
    return "User";
  }
  public static updateSubordinatesInverse(
    on: any,
    obj: any,
    type: string
  ): void {
    if ("Insert" === type) {
      on.addToSubordinates(obj as User);
    }

    if ("Delete" === type) {
      on.removeFromSubordinates(obj as User);
    }
  }
  public static updateMAccountInverse(on: any, obj: any, type: string): void {
    if ("Insert" === type) {
      on.addToMAccount(obj as MicroSoftAcc);
    }

    if ("Delete" === type) {
      on.removeFromMAccount(obj as MicroSoftAcc);
    }
  }
  public get firstName(): string {
    return this._firstName;
  }
  public setFirstName(val: string): void {
    let isValChanged: boolean = this._firstName !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._FIRSTNAME, this._firstName);

    this._firstName = val;

    this.fire("firstName", this);
  }
  public get lastName(): string {
    return this._lastName;
  }
  public setLastName(val: string): void {
    let isValChanged: boolean = this._lastName !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._LASTNAME, this._lastName);

    this._lastName = val;

    this.fire("lastName", this);
  }
  public get email(): string {
    return this._email;
  }
  public setEmail(val: string): void {
    let isValChanged: boolean = this._email !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._EMAIL, this._email);

    this._email = val;

    this.fire("email", this);
  }
  public get password(): string {
    return this._password;
  }
  public setPassword(val: string): void {
    let isValChanged: boolean = this._password !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._PASSWORD, this._password);

    this._password = val;

    this.fire("password", this);
  }
  public get phoneNumber(): string {
    return this._phoneNumber;
  }
  public setPhoneNumber(val: string): void {
    let isValChanged: boolean = this._phoneNumber !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._PHONENUMBER, this._phoneNumber);

    this._phoneNumber = val;

    this.fire("phoneNumber", this);
  }
  public get role(): UserRole {
    return this._role;
  }
  public setRole(val: UserRole): void {
    let isValChanged: boolean = this._role !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._ROLE, this._role.index);

    this._role = val;

    this.fire("role", this);
  }
  public get status(): boolean {
    return this._status;
  }
  public setStatus(val: boolean): void {
    let isValChanged: boolean = this._status !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._STATUS, this._status);

    this._status = val;

    this.fire("status", this);
  }
  public get profile(): DFile {
    return this._profile;
  }
  public setProfile(val: DFile): void {
    let isValChanged: boolean = this._profile !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._PROFILE, this._profile);

    this._profile = val;

    this.fire("profile", this);
  }
  public get isRememberMe(): boolean {
    return this._isRememberMe;
  }
  public setIsRememberMe(val: boolean): void {
    let isValChanged: boolean = this._isRememberMe !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._ISREMEMBERME, this._isRememberMe);

    this._isRememberMe = val;

    this.fire("isRememberMe", this);
  }
  public get twilioNumber(): string {
    return this._twilioNumber;
  }
  public setTwilioNumber(val: string): void {
    let isValChanged: boolean = this._twilioNumber !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._TWILIONUMBER, this._twilioNumber);

    this._twilioNumber = val;

    this.fire("twilioNumber", this);
  }
  public get subordinates(): Array<User> {
    return this._subordinates;
  }
  public setSubordinates(val: Array<User>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this._subordinates,
      val
    );

    if (!isValChanged) {
      return;
    }

    if (!this.d3eChanges.contains(User._SUBORDINATES)) {
      let _old: Array<User> = Array.from(this._subordinates);

      this.updateD3EChanges(User._SUBORDINATES, _old);
    }

    this.updateObservableColl("subordinates", this._subordinates, val);

    this._subordinates.clear();

    this._subordinates.addAll(val);

    this.fire("subordinates", this);
  }
  public addToSubordinates(val: User, index: number = -1): void {
    let _old: Array<User> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(User._SUBORDINATES);

    if (_isNewChange) {
      _old = Array.from(this._subordinates);
    }

    if (index === -1) {
      if (!this._subordinates.contains(val)) this._subordinates.add(val);
    } else {
      this._subordinates.remove(this._subordinates.elementAt(index));

      this._subordinates.add(val);
    }

    this.fire("subordinates", this, val, true);

    this.updateObservable("subordinates", null, val);

    if (_isNewChange) {
      this.updateD3EChanges(User._SUBORDINATES, _old);
    }
  }
  public removeFromSubordinates(val: User): void {
    let _old: Array<User> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(User._SUBORDINATES);

    if (_isNewChange) {
      _old = Array.from(this._subordinates);
    }

    this._subordinates.remove(val);

    this.fire("subordinates", this, val, false);

    if (_isNewChange) {
      this.updateD3EChanges(User._SUBORDINATES, _old);
    }
  }
  public get mAccount(): MicroSoftAcc {
    return this._mAccount;
  }
  public setMAccount(val: MicroSoftAcc): void {
    let isValChanged: boolean = this._mAccount !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._MACCOUNT, this._mAccount);

    this.updateObservable("mAccount", this._mAccount, val);

    this._mAccount = val;

    this.fire("mAccount", this);
  }
  public get reportingManager(): User {
    return this._reportingManager;
  }
  public setReportingManager(val: User): void {
    let isValChanged: boolean = this._reportingManager !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._REPORTINGMANAGER, this._reportingManager);

    this.updateObservable("reportingManager", this._reportingManager, val);

    this._reportingManager = val;

    this.fire("reportingManager", this);
  }
  public get lastHeartBeat(): DateTime {
    return this._lastHeartBeat;
  }
  public setLastHeartBeat(val: DateTime): void {
    let isValChanged: boolean = this._lastHeartBeat !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._LASTHEARTBEAT, this._lastHeartBeat);

    this._lastHeartBeat = val;

    this.fire("lastHeartBeat", this);
  }
  public get online(): boolean {
    return this._online;
  }
  public setOnline(val: boolean): void {
    let isValChanged: boolean = this._online !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._ONLINE, this._online);

    this._online = val;

    this.fire("online", this);
  }
  public get company(): Company {
    return this._company;
  }
  public setCompany(val: Company): void {
    let isValChanged: boolean = this._company !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._COMPANY, this._company);

    this.updateObservable("company", this._company, val);

    this._company = val;

    this.fire("company", this);
  }
  public get createdDate(): DateTime {
    return this._createdDate;
  }
  public setCreatedDate(val: DateTime): void {
    let isValChanged: boolean = this._createdDate !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(User._CREATEDDATE, this._createdDate);

    this._createdDate = val;

    this.fire("createdDate", this);
  }
  public get(field: number): any {
    switch (field) {
      case User._FIRSTNAME: {
        return this._firstName;
      }

      case User._LASTNAME: {
        return this._lastName;
      }

      case User._EMAIL: {
        return this._email;
      }

      case User._PASSWORD: {
        return this._password;
      }

      case User._PHONENUMBER: {
        return this._phoneNumber;
      }

      case User._ROLE: {
        return this._role.index;
      }

      case User._STATUS: {
        return this._status;
      }

      case User._PROFILE: {
        return this._profile;
      }

      case User._ISREMEMBERME: {
        return this._isRememberMe;
      }

      case User._TWILIONUMBER: {
        return this._twilioNumber;
      }

      case User._SUBORDINATES: {
        return this._subordinates;
      }

      case User._MACCOUNT: {
        return this._mAccount;
      }

      case User._REPORTINGMANAGER: {
        return this._reportingManager;
      }

      case User._LASTHEARTBEAT: {
        return this._lastHeartBeat;
      }

      case User._ONLINE: {
        return this._online;
      }

      case User._COMPANY: {
        return this._company;
      }

      case User._CREATEDDATE: {
        return this._createdDate;
      }
      default: {
        return super.get(field);
      }
    }
  }
  public updateD3EChanges(index: number, value: any): void {
    if (this.lockedChanges()) {
      return;
    }

    super.updateD3EChanges(index, value);
  }
  public restore(): void {
    /*
TODO: Might be removed
*/

    this.d3eChanges.restore(this);
  }
  public deepClone(clearId = true): User {
    let ctx: CloneContext = new CloneContext({ "clearId": clearId });

    return ctx.startClone(this);
  }
  public collectChildValues(ctx: CloneContext): void {
    super.collectChildValues(ctx);
  }
  public deepCloneIntoObj(dbObj: DBObject, ctx: CloneContext): void {
    super.deepCloneIntoObj(dbObj, ctx);

    let obj: User = dbObj as User;

    obj.setFirstName(this._firstName);

    obj.setLastName(this._lastName);

    obj.setEmail(this._email);

    obj.setPassword(this._password);

    obj.setPhoneNumber(this._phoneNumber);

    obj.setRole(this._role);

    obj.setStatus(this._status);

    obj.setProfile(this._profile);

    obj.setIsRememberMe(this._isRememberMe);

    obj.setTwilioNumber(this._twilioNumber);

    obj.setSubordinates(this._subordinates);

    obj.setMAccount(this._mAccount);

    obj.setReportingManager(this._reportingManager);

    obj.setLastHeartBeat(this._lastHeartBeat);

    obj.setOnline(this._online);

    obj.setCompany(this._company);

    obj.setCreatedDate(this._createdDate);
  }
  public set(field: number, value: any): void {
    switch (field) {
      case User._FIRSTNAME: {
        this.setFirstName(value as string);
        break;
      }

      case User._LASTNAME: {
        this.setLastName(value as string);
        break;
      }

      case User._EMAIL: {
        this.setEmail(value as string);
        break;
      }

      case User._PASSWORD: {
        this.setPassword(value as string);
        break;
      }

      case User._PHONENUMBER: {
        this.setPhoneNumber(value as string);
        break;
      }

      case User._ROLE: {
        this.setRole(UserRole.values[value as number]);
        break;
      }

      case User._STATUS: {
        this.setStatus(value as boolean);
        break;
      }

      case User._PROFILE: {
        this.setProfile(value as DFile);
        break;
      }

      case User._ISREMEMBERME: {
        this.setIsRememberMe(value as boolean);
        break;
      }

      case User._TWILIONUMBER: {
        this.setTwilioNumber(value as string);
        break;
      }

      case User._SUBORDINATES: {
        this.setSubordinates((value as Array<any>).cast<User>().toList());
        break;
      }

      case User._MACCOUNT: {
        this.setMAccount(value as MicroSoftAcc);
        break;
      }

      case User._REPORTINGMANAGER: {
        this.setReportingManager(value as User);
        break;
      }

      case User._LASTHEARTBEAT: {
        this.setLastHeartBeat(value as DateTime);
        break;
      }

      case User._ONLINE: {
        this.setOnline(value as boolean);
        break;
      }

      case User._COMPANY: {
        this.setCompany(value as Company);
        break;
      }

      case User._CREATEDDATE: {
        this.setCreatedDate(value as DateTime);
        break;
      }
      default: {
        super.set(field, value);
      }
    }
  }
}
